/* eslint-disable react/jsx-filename-extension */
import get from 'lodash/get';
import { spacing } from '@nerdwallet/base-styles/variables';
import {
  stringSelectSpec,
  numberInputSpec,
  group,
  groupButton,
  wrapper,
  stringTextAreaSpec,
  boolCheckboxSpec,
} from '../../../lib/editing-configs';
import getTextColor from '../../../lib/text-color-config';
import { BoldIcon } from './assets/bold-icon';
import { ItalicIcon } from './assets/italic-icon';
import { UnderlineIcon } from './assets/underline-icon';
import { LeftIcon } from './assets/left-icon';
import { CenterIcon } from './assets/center-icon';
import { RightIcon } from './assets/right-icon';
import { StrikethroughIcon } from './assets/strikethrough';
import { CodeIcon } from './assets/code-icon';

const titleSizes = [
  { label: 'Default', value: 'default' },
  { label: 'Small', value: 'small' },
];

const textSizes = [
  { label: 'Default', value: 'default' },
  { label: 'Large', value: 'large' },
  { label: 'Small', value: 'small' },
  { label: 'X-Small', value: 'x-small' },
];

const textTypes = [
  { label: 'Title (Currency)', value: 'title' },
  { label: 'Text (Currency)', value: 'text' },
  { label: 'Accent (Currency)', value: 'accent' },
  { label: 'Custom', value: 'custom' },
];

const moveArrayItemPosition = (array, oldIndex, newIndex) => {
  let resultIndex = newIndex;
  if (newIndex >= array.length) {
    resultIndex = array.length - 1;
  }
  array.splice(resultIndex, 0, array.splice(oldIndex, 1)[0]);
  return array;
};

// Parsing base-styles spacing values to array and removing "--wide" entries
const mapCurrencySpacingSizes = Array.from(
  new Map(Object.entries(spacing)),
  ([key, value]) => ({ label: key, value })
).filter((item) => !item.label.includes('--wide'));

// Let's use "moveArrayItemPosition" to prevent the "3-half" size from being in the last position
const currencySpacingSizes = moveArrayItemPosition(
  mapCurrencySpacingSizes,
  6,
  4
);

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// Once Top, Left, Right and bottom will be the same for both padding and margin
// let's avoid repeatable code and use this builder to handle this.
const buildSpacingInputs = (
  inputComponent,
  fielName,
  fieldList = [],
  extraLabel = '',
  isSelectInput
) => {
  const help = {
    help: 'Defaults to "0"',
  };
  if (isSelectInput) {
    return fieldList.map((input) =>
      inputComponent(
        `${fielName}.${input}`,
        `${capitalize(input)}`,
        currencySpacingSizes,
        help
      )
    );
  }
  return fieldList.map((input) =>
    inputComponent(
      `${fielName}.${input}`,
      `${capitalize(input)} ${extraLabel}`,
      help
    )
  );
};

export const getEditingConfig = (
  componentDocument,
  fieldLabel = 'Text',
  allowedControls = [],
  fieldName = 'universalText',
  options
) => {
  const fontFamily = get(
    componentDocument,
    `props.${fieldName}.fontFamily`,
    'Default'
  );
  const isTextTitle =
    get(componentDocument, `props.${fieldName}.textType`, 'title') === 'title';
  const shouldDisplayFontWeight =
    get(componentDocument, `props.${fieldName}.textType`, 'title') === 'text' &&
    fontFamily !== 'Obviously';
  const shouldDisplayCustomTextSize =
    get(componentDocument, `props.${fieldName}.textType`, 'title') === 'custom';
  const shouldDisplayCustomPadding =
    get(componentDocument, `props.${fieldName}.padding.type`, 'currency') ===
    'custom';
  const shouldDisplayCustomMargin =
    get(componentDocument, `props.${fieldName}.margin.type`, 'currency') ===
    'custom';

  const paddingType = get(
    componentDocument,
    `props.${fieldName}.padding.type`,
    'currency'
  );
  const marginType = get(
    componentDocument,
    `props.${fieldName}.margin.type`,
    'currency'
  );
  const textType = get(componentDocument, `props.${fieldName}.textType`, null);

  const groupButtonsInputConfig = [
    {
      label: 'Bold',
      value: 'bold',
      component: <BoldIcon />,
      multiple: true,
    },
    {
      label: 'Italic',
      value: 'italic',
      component: <ItalicIcon />,
      multiple: true,
    },
    {
      label: 'Underline',
      value: 'underline',
      component: <UnderlineIcon />,
      multiple: true,
    },
    {
      label: 'Strikethrough',
      value: 'strikethrough',
      component: <StrikethroughIcon />,
      multiple: true,
    },
    {
      label: 'HTML output',
      value: 'htmlOutput',
      component: <CodeIcon />,
      multiple: true,
    },
    { label: 'Left', value: 'left', component: <LeftIcon /> },
    { label: 'Center', value: 'center', component: <CenterIcon /> },
    { label: 'Right', value: 'right', component: <RightIcon /> },
  ];

  const groupButtonsEditorControls = allowedControls.includes('groupButton')
    ? [
        groupButton(
          `${fieldName}.groupButton`,
          'Styles',
          groupButtonsInputConfig,
          {
            help: 'The Text Styles',
            multipleItems: ['bold', 'italic', 'underline'],
          }
        ),
      ]
    : [];

  const paddingInputConfig = shouldDisplayCustomPadding
    ? buildSpacingInputs(
        numberInputSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomPadding
      );

  const paddingEditorControls = allowedControls.includes('padding')
    ? [
        group('Padding', [
          stringSelectSpec(
            `${fieldName}.padding.type`,
            'Padding Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...paddingInputConfig,
        ]),
      ]
    : [];

  const marginInputConfig = shouldDisplayCustomMargin
    ? buildSpacingInputs(
        numberInputSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomMargin
      );

  const marginEditorControls = allowedControls.includes('margin')
    ? [
        group('Margin', [
          stringSelectSpec(
            `${fieldName}.margin.type`,
            'Margin Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...marginInputConfig,
        ]),
      ]
    : [];

  const fontWeightInputConfig = shouldDisplayFontWeight
    ? [
        stringSelectSpec(
          `${fieldName}.fontWeight`,
          'Font Weight',
          [
            { label: '300', value: '300' },
            { label: '500', value: '500' },
          ],
          {
            help: 'Defaults to 300',
          }
        ),
      ]
    : [];

  const fontWeightEditorControls = allowedControls.includes('fontWeight')
    ? [...fontWeightInputConfig]
    : [];

  const textSizeInputConfig = shouldDisplayCustomTextSize
    ? [
        numberInputSpec(`${fieldName}.customTextSize`, 'Text Size', {
          help: 'Default to "16"',
        }),
      ]
    : [
        stringSelectSpec(
          `${fieldName}.textSize`,
          'Text Size',
          isTextTitle ? titleSizes : textSizes
        ),
      ];

  const textSizeEditorControls = allowedControls.includes('textSize')
    ? [...textSizeInputConfig]
    : [];

  const fontFamilyInputEditorControls = allowedControls.includes('fontFamily')
    ? [
        stringSelectSpec(`${fieldName}.fontFamily`, 'Font Family', [
          { label: 'Default', value: 'Default' },
          { label: 'Chronicle', value: 'Chronicle' },
          { label: 'Obviously', value: 'Obviously' },
          { label: 'Gotham Black', value: 'GothamBlack' },
        ]),
      ]
    : [];

  const textTypeEditorControls = allowedControls.includes('textType')
    ? [
        stringSelectSpec(
          `${fieldName}.textType`,
          'Text Type',
          // when we don't have a "textType" selected, lets have a value from
          // options and place it at the top of the dropdown selector
          !textType && options?.text?.textType
            ? [
                ...textTypes.sort((item) =>
                  item.value === options?.text?.textType ? -1 : 1
                ),
              ]
            : [...textTypes],
          {
            help: 'Use to select Title, Text, Accent or Custom text output. Note: this defaults',
          }
        ),
      ]
    : [];

  const textColorEditorControls = allowedControls.includes('textColor')
    ? [
        group(
          'Text Color',
          [
            ...getTextColor.getEditingConfig(
              componentDocument,
              fieldName,
              'Text Color'
            ),
          ],
          {
            help: 'Choose a Currency text color or select Hexcolor from the dropdown to use a hexcolor value instead',
          }
        ),
      ]
    : [];

  const editorPanelOptions = [
    stringTextAreaSpec(`${fieldName}.text`, 'Text', {
      help: 'When "Interpolated Query Value" is checked, use "{{some text}}" for text interpolation.',
    }),
    ...groupButtonsEditorControls,
    ...textTypeEditorControls,
    ...fontFamilyInputEditorControls,
    ...textSizeEditorControls,
    ...fontWeightEditorControls,
    boolCheckboxSpec(
      `${fieldName}.enableInterpolatedQueryValue`,
      'Enable Interpolated Query Value'
    ),
    ...textColorEditorControls,
    ...paddingEditorControls,
    ...marginEditorControls,
  ];

  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};
